import { useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Wallet, Plus, Trash2, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { MAINTENANCE_MODE } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PortfolioPanel = ({ portfolio, currentPrice, onRefresh }) => {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [notes, setNotes] = useState('');
  const [adding, setAdding] = useState(false);

  const addHolding = async () => {
    if (MAINTENANCE_MODE) {
      toast.error('Portfolio management is temporarily disabled during maintenance.');
      return;
    }
    
    if (!amount || !purchasePrice) {
      toast.error('Please fill in all required fields');
      return;
    }

    setAdding(true);
    try {
      await axios.post(`${API}/portfolio`, {
        amount: parseFloat(amount),
        purchase_price: parseFloat(purchasePrice),
        notes
      });
      toast.success('Holding added successfully!');
      setAmount('');
      setPurchasePrice('');
      setNotes('');
      setOpen(false);
      onRefresh();
    } catch (error) {
      console.error('Error adding holding:', error);
      toast.error(error.response?.data?.detail || 'Failed to add holding');
    } finally {
      setAdding(false);
    }
  };

  const deleteHolding = async (id) => {
    if (MAINTENANCE_MODE) {
      toast.error('Portfolio management is temporarily disabled during maintenance.');
      return;
    }
    
    try {
      await axios.delete(`${API}/portfolio/${id}`);
      toast.success('Holding removed');
      onRefresh();
    } catch (error) {
      console.error('Error deleting holding:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove holding');
    }
  };

  const calculatePnL = (holding) => {
    if (!currentPrice) return { value: 0, percent: 0 };
    const currentValue = holding.amount * currentPrice;
    const purchaseValue = holding.amount * holding.purchase_price;
    const pnl = currentValue - purchaseValue;
    const pnlPercent = (pnl / purchaseValue) * 100;
    return { value: pnl, percent: pnlPercent };
  };

  return (
    <Card className="p-6 h-full flex flex-col">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Wallet className="w-5 h-5 text-emerald-500" />
          <h3 className="text-xl font-heading font-semibold tracking-tight">Portfolio</h3>
        </div>
        <Dialog open={open} onOpenChange={(v) => !MAINTENANCE_MODE && setOpen(v)}>
          <DialogTrigger asChild>
            <Button 
              data-testid="add-holding-btn" 
              size="sm" 
              disabled={MAINTENANCE_MODE}
              className="bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4 mr-1" />
              {MAINTENANCE_MODE ? 'Unavailable' : 'Add'}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add BTC Holding</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4" aria-describedby="add-holding-description">
              <p id="add-holding-description" className="sr-only">Enter the amount of Bitcoin and purchase price to add to your portfolio</p>
              <div>
                <label className="text-sm font-medium mb-2 block">Amount (BTC)</label>
                <Input
                  data-testid="holding-amount-input"
                  type="number"
                  step="0.00000001"
                  placeholder="0.001"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Purchase Price (USD)</label>
                <Input
                  data-testid="holding-price-input"
                  type="number"
                  step="0.01"
                  placeholder="50000"
                  value={purchasePrice}
                  onChange={(e) => setPurchasePrice(e.target.value)}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Notes (optional)</label>
                <Input
                  data-testid="holding-notes-input"
                  placeholder="Long-term hold"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
              <Button
                data-testid="submit-holding-btn"
                onClick={addHolding}
                disabled={adding}
                className="w-full"
              >
                {adding ? 'Adding...' : 'Add Holding'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex-1 overflow-y-auto space-y-3">
        <AnimatePresence>
          {portfolio.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-8">
              <Wallet className="w-12 h-12 text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground">No holdings yet</p>
              <p className="text-xs text-muted-foreground mt-1">Add your first BTC holding</p>
            </div>
          ) : (
            portfolio.map((holding, index) => {
              const pnl = calculatePnL(holding);
              return (
                <motion.div
                  key={holding.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-4 rounded-xl border border-border/50 bg-card hover:shadow-md transition-all"
                  data-testid={`holding-${holding.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-lg font-heading font-semibold">{holding.amount} BTC</p>
                      <p className="text-xs text-muted-foreground">
                        Bought at ${holding.purchase_price.toLocaleString()}
                      </p>
                    </div>
                    <Button
                      data-testid={`delete-holding-${holding.id}`}
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteHolding(holding.id)}
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className={`flex items-center gap-2 text-sm font-medium ${pnl.value >= 0 ? 'text-gain' : 'text-loss'}`}>
                    {pnl.value >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                    <span>
                      {pnl.value >= 0 ? '+' : ''}${pnl.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    <span>({pnl.percent.toFixed(2)}%)</span>
                  </div>
                  {holding.notes && (
                    <p className="text-xs text-muted-foreground mt-2 italic">{holding.notes}</p>
                  )}
                </motion.div>
              );
            })
          )}
        </AnimatePresence>
      </div>
    </Card>
  );
};

export default PortfolioPanel;